import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
    AttributesValueType,
    CompanyAttributeDTO,
    CompanyProductSelectionAttributeWithOverrideInformation
} from '@iota/iota-management-portal-v4-client';
import { AttributeValueTypeDescriptions, AttributeValueTypes } from '../../../constants-and-enums/attribute-value-types';
import { AttributeDetailsDirective, AttributeDetailsInterface } from '../attribute-details.directive';

@Component({
    standalone: false,
    selector: 'app-attribute-v2',
    templateUrl: './attribute-v2.component.html'
})
export class AttributeV2Component extends AttributeDetailsDirective implements AttributeDetailsInterface, OnInit, OnChanges {
    public get invalid(): boolean {
        return (
            this.attributeForm.invalid ||
            this.attributeForm.controls.attributeValue.value.trim() === '' ||
            this.attributeForm.controls.attributeName.value.trim() === ''
        );
    }

    @Input() usedAttributeNames: string[];
    nameAlreadyUsed = false;

    @Input() deletable = true;

    attributeForm: UntypedFormGroup;
    isStringAttribute: boolean;
    isOverride = false;

    attributeValueTypes = AttributeValueTypes;

    updatedValueType: AttributesValueType;
    updatedValue = '';
    updatedName = '';
    updatedLocale = '';

    possibleLocales: string[] = [];

    constructor(datePipe: DatePipe) {
        super(datePipe);
    }

    initAttribute(valueAttribute: CompanyAttributeDTO) {
        const prevMarkedForUpsert = this.markedForUpsert ?? false;
        const prevMarkedForDelete = this.markedForDelete ?? false;

        this.updatedValueType = valueAttribute?.valueType ?? AttributesValueType.STRING;
        if (this.updatedValueType === AttributesValueType.STRING) this.isStringAttribute = true;

        this.updatedValue = this.parseAttributeValue(valueAttribute, this.updatedValueType);
        this.updatedName = valueAttribute?.attributeName ?? '';
        this.updatedLocale = valueAttribute?.locale ?? this.locales?.[0];

        this.possibleLocales = [...this.locales];
        // If the attribute has a "custom" locale, keep it possible
        if (valueAttribute?.locale && !this.locales.includes(valueAttribute.locale)) {
            this.possibleLocales = [valueAttribute.locale];
        }

        if (this.markIfParentIdIsThisParentId && this.parentId && this.parentId === this.currentAttribute?.parentSkId) {
            this.showEntitySpecificTooltip = true;
            this.isOverride = this.isAttributeWithOverrideInformation(valueAttribute) && valueAttribute.override;
        }

        if (!this.attributeForm) {
            this.attributeForm = new UntypedFormGroup({
                attributeName: new UntypedFormControl(Validators.required),
                attributeValueType: new UntypedFormControl(Validators.required),
                attributeValue: new UntypedFormControl(Validators.required),
                attributeLocale: new UntypedFormControl()
            });
        }

        this.attributeForm.patchValue({
            attributeName: this.updatedName,
            attributeValueType: this.updatedValueType,
            attributeValue: this.updatedValue,
            attributeLocale: this.updatedLocale
        });

        if (!this.editMode) {
            this.attributeForm.disable();
        } else {
            this.enableForm();
        }

        this.markedForDelete = false;

        this.markedForUpsert = !this.previousAttribute?.attributeId || this.someValuesChanged();
        if (!this.previousAttribute?.attributeId && prevMarkedForUpsert !== this.markedForUpsert) {
            this.updateMarkForUpsertValue.emit(this.markedForUpsert ? 1 : -1);
        }
        // Fix delete badge (only needed if attribute was imported and is now marked for upsert)
        if (this.markedForDelete !== prevMarkedForDelete) {
            this.updateMarkForDeleteValue.emit(this.markedForDelete ? 1 : -1);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        const prevAttribute = changes?.currentAttribute?.previousValue;
        const currentAttribute = changes?.currentAttribute?.currentValue;
        if (prevAttribute !== currentAttribute) {
            this.initAttribute(this.currentAttribute);
        }

        const currentEditMode = changes?.editMode?.currentValue;
        if (currentEditMode) {
            this.enableForm();
        }
    }

    ngOnInit(): void {
        this.initAttribute(this.currentAttribute);

        this.attributeForm.valueChanges.subscribe((value) => {
            this.attributeForm.controls.attributeValue.setErrors(null);

            // We need to extract the value type this way in case the input is disabled,
            // because then value.attributeValueType is null
            const valueType = this.attributeForm.getRawValue()?.attributeValueType;

            this.updatedValueType = valueType;
            if (this.updatedValueType === AttributesValueType.STRING) this.isStringAttribute = true;

            this.updatedValue = value.attributeValue;
            this.updatedName = value.attributeName.trim();
            this.nameAlreadyUsed =
                this.updatedName !== this.currentAttribute?.attributeName && this.usedAttributeNames?.includes(this.updatedName);

            if (value.attributeLocale && valueType === AttributesValueType.STRING && value.attributeLocale.trim() !== '') {
                this.updatedLocale = value.attributeLocale;
            }

            const prevMarked = this.markedForUpsert ?? false;
            this.markedForUpsert = this.someValuesChanged();
            if (prevMarked !== this.markedForUpsert) {
                this.updateMarkForUpsertValue.emit(this.markedForUpsert ? 1 : -1);
            }
        });
    }

    enableForm() {
        this.attributeForm?.enable();
        if (this.previousAttribute?.attributeId) {
            this.attributeForm.controls?.attributeValueType?.disable();
        }
    }

    markForDelete() {
        if (this.previousAttribute?.attributeId) {
            this.markedForDelete = true;
            this.attributeForm.disable();
            this.updateMarkForDeleteValue.emit(1);
            if (this.markedForUpsert) this.updateMarkForUpsertValue.emit(-1);
        } else {
            this.removeAttribute.emit();
            this.updateMarkForUpsertValue.emit(-1);
        }
    }

    undoMarkForDelete() {
        this.markedForDelete = false;
        this.updateMarkForDeleteValue.emit(-1);
        this.enableForm();
        if (this.markedForUpsert) this.updateMarkForUpsertValue.emit(1);
    }

    someValuesChanged() {
        const initialValue = this.parseAttributeValue(this.previousAttribute, this.updatedValueType);
        return (
            this.updatedValue !== initialValue ||
            this.updatedName !== this.previousAttribute?.attributeName ||
            this.updatedValueType !== this.previousAttribute?.valueType ||
            (this.updatedValueType === AttributesValueType.STRING && this.updatedLocale !== this.previousAttribute?.locale)
        );
    }

    getUpsertedAttribute(): CompanyAttributeDTO {
        if (!this.markedForUpsert) return undefined;

        const attribute: CompanyAttributeDTO | CompanyProductSelectionAttributeWithOverrideInformation = {
            ...this.previousAttribute,
            attributeName: this.updatedName,
            valueType: this.updatedValueType,
            locale: this.updatedValueType === AttributesValueType.STRING ? this.updatedLocale : undefined,
            ...this.getAttributeValueArray(this.updatedValue, this.updatedValueType)
        };

        // If the parentId is set but not the current parentId, then we need to override this attribute, so we remove the audit values and set the parentId
        if (!!attribute.parentSkId && this.parentId !== attribute.parentSkId) {
            this.clearAttributeAuditFieldsAndIds(attribute);
            attribute.parentSkId = this.parentId;
        }

        return attribute;
    }

    getValueTypeDescription(type: string): string {
        return AttributeValueTypeDescriptions.get(type);
    }

    resetAttribute() {
        this.initAttribute(this.previousAttribute);
    }
}
